import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import type { SSEResponse, SSEStreamService } from '@patsnap-utils/sse';
import { AITranslationModel } from '../model/ai-translation.model';
import type { AITranslationState, ContentItem } from '../type/ai-translation.type';
import { Step, StreamStatus } from '../type/ai-translation.enum';
import type { ApiTranslationSyncRequest, ApiTranslationSyncResponse } from '@/infrastructure/api-functions/interface/translation-sync.interface';
import { TranslationSync } from '@/infrastructure/api-functions/interface/translation-sync.interface';
import { StreamService, StreamEvents } from '@/services/ai-translation/service/stream.service.interface'


@injectable()
export class TranslationGeneratorSyncServiceImpl implements StreamService<ContentItem[]> {
    private _stream: SSEStreamService<any> | null = null;
    private _onStatusChangeEmitter = new Emitter<string>();
    readonly onStatusChange = this._onStatusChangeEmitter.event;
    private _onContentChangeEmitter = new Emitter<ContentItem[]>();
    readonly onContentChange = this._onContentChangeEmitter.event;
    private _onStatesChangeEmitter = new Emitter<AITranslationState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(AITranslationModel)
        private _aiTranslationModel: AITranslationModel,
        @inject(TranslationSync)
        private _translationSync: TranslationSync,
    ) { }

    private _setAITranslationModelData(value: Partial<AITranslationState>) {
        this._aiTranslationModel.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public status = StreamStatus.PROGRESS_WITHOUT_DATA;

    private _fetchStreamData(input: ApiTranslationSyncRequest, streamEvents?: StreamEvents<ApiTranslationSyncResponse>): void {
        this._stream = this._translationSync.translationSync(input)
            .onOpen((response: Response) => {
                streamEvents?.onOpen && streamEvents.onOpen(response);
            })
            .onMessage((data: SSEResponse<ApiTranslationSyncResponse>) => {
                const { status } = data?.data ?? {};
                this._onStatusChangeEmitter.fire(status);
                if (status === StreamStatus.FINISH) {
                    this._stream?.close();
                } else {
                    const content = data?.data?.content?.map(item => {
                        return {
                            original: item.src_text,
                            translation: item.translated_text,
                            graphIndex: item.graph_index,
                        };
                    }) || [];

                    if (content.length) {
                        this._setAITranslationModelData({ result: content });
                        this._onContentChangeEmitter.fire(content);
                    }
                }
            })
            .onError((err: any) => {
                streamEvents?.onError && streamEvents.onError(err);
            })
            .onClose(() => {
                streamEvents?.onClose && streamEvents.onClose();
            });

        this._stream?.start();
    }

    public execute() {
        this.status = StreamStatus.PROGRESS_WITHOUT_DATA;
        this._onStatusChangeEmitter.fire(this.status);

        this._fetchStreamData({
            current_step: Step.RESULT,
            task_id: this._aiTranslationModel.modelData?.taskId,
        });
    }

    public abort() {
        this.status = StreamStatus.ABORTED;

        this._stream?.close();
        this._onStatusChangeEmitter.fire(this.status);
    }

    public executeRegenerate() {
        this.abort();
        this._onContentChangeEmitter.fire([]);
        this.execute();
    }

    public stopLoading() {
        this.abort();
        this._setAITranslationModelData({
            step: Step.ORIGINAL_INPUT
        });
    }
}